import { z } from 'zod';
import { Dateish } from './common';

// ── entrega de mensagens (chat_messages.delivery_status, v6) ────────────────
/** ack do broker/Meta para mensagens outbound: sent < delivered < read; failed é terminal */
export const DeliveryStatusSchema = z.enum(['sent', 'delivered', 'read', 'failed']);
export type DeliveryStatus = z.infer<typeof DeliveryStatusSchema>;

/**
 * O que a tela diz da entrega de uma mensagem que saiu (20/10). `sent` no banco
 * é "o broker aceitou", e não "chegou ao WhatsApp do cliente": a Blip não manda
 * o "enviado" do WhatsApp, e um número que não existe nunca devolve nada, nem
 * entregue nem falha. Sete cobranças para números sem o 55 ficaram "Enviada"
 * sem ter chegado a ninguém. Passado o prazo sem confirmação, a tela diz isso.
 */
export const DELIVERY_UNCONFIRMED_AFTER_MS = 10 * 60_000;
export type DeliveryView = 'pending' | 'unconfirmed' | 'delivered' | 'read' | 'failed';

/** A MESMA regra nas duas telas que desenham a entrega (a conversa e a linha do tempo). */
export function deliveryView(status: DeliveryStatus, sentAt: string | Date, now: number): DeliveryView {
  if (status !== 'sent') return status;
  const t = new Date(sentAt).getTime();
  if (!Number.isFinite(t)) return 'pending';
  return now - t >= DELIVERY_UNCONFIRMED_AFTER_MS ? 'unconfirmed' : 'pending';
}

/**
 * Por que uma mensagem de saída NÃO foi entregue (spec 028, FR-006) — o motivo
 * curto que a bolha mostra ao lado do "Não entregue". Gravado junto com o
 * delivery_status 'failed' (chat_messages.delivery_error), nunca sozinho:
 * - window_closed: janela de 24h fechada — o broker pediu modelo (422
 *   template_required) ou a Meta devolveu 131047/470;
 * - rejected: o broker recusou o pedido de vez (400, 409, 422, ou 2xx sem fila)
 *   — tentar de novo o MESMO pedido só adianta depois que algo mudar do lado dele;
 * - denied: número na lista de bloqueio do broker (denylist);
 * - send_failed: o broker não respondeu em nenhuma das tentativas (rede, 5xx);
 * - undelivered: o broker aceitou, e o WhatsApp devolveu `failed` depois;
 * - postponed: o envio NÃO saiu e vai sair depois (spec 025, fase 3) — o contato
 *   está em atendimento em outro serviço da Blip (`contact_busy_in_other_service`),
 *   a Blip está desligada no broker (`blip_disabled`) ou o disparo pela Blip foi
 *   parado pelo interruptor (`blip_dispatch_stopped`). Nada chegou ao contato, e o
 *   disparo volta para a fila — não é dado inválido.
 * `code` é o código de quem recusou (o do broker ou o da Meta/Blip), quando veio.
 */
export const DeliveryErrorReasonSchema = z.enum([
  'window_closed',
  'rejected',
  'denied',
  'send_failed',
  'undelivered',
  'postponed',
]);
export type DeliveryErrorReason = z.infer<typeof DeliveryErrorReasonSchema>;

export const DeliveryErrorSchema = z.object({
  reason: DeliveryErrorReasonSchema,
  code: z.string().nullable(),
});
export type DeliveryError = z.infer<typeof DeliveryErrorSchema>;

export const DELIVERY_ERROR_LABELS: Record<DeliveryErrorReason, string> = {
  window_closed: 'Janela de 24h fechada — envie um modelo',
  rejected: 'Recusada pelo provedor',
  denied: 'Número bloqueado para envio',
  send_failed: 'O provedor não respondeu',
  undelivered: 'O WhatsApp não entregou',
  postponed: 'Adiado — o disparo tenta de novo mais tarde',
};

/** código de quem recusou: curto e sem nada além de [A-Za-z0-9_.-] — vai para a tela */
const DELIVERY_ERROR_CODE = /^[A-Za-z0-9_.-]{1,40}$/;

/** grafia na coluna: `<motivo>` ou `<motivo>:<código>` (código fora do formato cai fora) */
export function formatDeliveryError(error: DeliveryError): string {
  return error.code !== null && DELIVERY_ERROR_CODE.test(error.code)
    ? `${error.reason}:${error.code}`
    : error.reason;
}

/** a coluna → o wire; motivo desconhecido (versão futura) vira `rejected`, nunca some */
export function parseDeliveryError(raw: string | null | undefined): DeliveryError | null {
  if (!raw) return null;
  const sep = raw.indexOf(':');
  const reasonRaw = sep === -1 ? raw : raw.slice(0, sep);
  const codeRaw = sep === -1 ? null : raw.slice(sep + 1);
  const reason = DeliveryErrorReasonSchema.safeParse(reasonRaw);
  return {
    reason: reason.success ? reason.data : 'rejected',
    code: codeRaw !== null && DELIVERY_ERROR_CODE.test(codeRaw) ? codeRaw : null,
  };
}

// ── integração (GET|PUT /whatsapp/integration) ──────────────────────────────
export const WhatsappIntegrationSchema = z.object({
  /** a api key NUNCA volta inteira: só os últimos 4 (`****abcd`); null = não configurada */
  apiKeyMasked: z.string().nullable(),
  businessId: z.string().nullable(),
  /** webhook já registrado no manager (POST /whatsapp/integration/webhook) */
  webhookRegistered: z.boolean(),
  active: z.boolean(),
});
export type WhatsappIntegration = z.infer<typeof WhatsappIntegrationSchema>;

export const WhatsappIntegrationPutBodySchema = z.object({
  /** org key do zeepchat (`zeep_...`) */
  apiKey: z.string().min(10),
  businessId: z.string().optional(),
});
export type WhatsappIntegrationPutBody = z.infer<typeof WhatsappIntegrationPutBodySchema>;

// ── chatbot por canal (v8 Etapa 4) ──────────────────────────────────────────
/** limites do menu do chatbot — API valida, UI de settings mostra os caps */
export const CHATBOT_MENU_MAX_OPTIONS = 10;
export const CHATBOT_LABEL_MAX = 72;
export const CHATBOT_REPLY_MAX = 1024;

/**
 * Opção do menu numerado do chatbot: `label` vira a linha "N. <label>" no
 * texto enviado, `deskId` é o departamento que a resposta casada atribui ao
 * chat e `reply` (opcional) é a confirmação enviada após o match.
 */
export const WhatsappChatbotMenuOptionSchema = z.object({
  label: z.string().trim().min(1).max(CHATBOT_LABEL_MAX),
  deskId: z.number().int().positive(),
  reply: z.string().min(1).max(CHATBOT_REPLY_MAX).nullish(),
});
export type WhatsappChatbotMenuOption = z.infer<typeof WhatsappChatbotMenuOptionSchema>;

/**
 * Estilo do menu do chatbot (v10 Etapa 1): 'interactive' = lista nativa do
 * WhatsApp (rows roteadas por id `cbopt:<n>`, com fallback determinístico para
 * texto quando o broker rejeita); 'text' = menu numerado clássico. Default
 * 'interactive' (coluna e schema).
 */
export const WhatsappChatbotMenuStyleSchema = z.enum(['interactive', 'text']);
export type WhatsappChatbotMenuStyle = z.infer<typeof WhatsappChatbotMenuStyleSchema>;

// ── modo de bot do canal (spec 021) ─────────────────────────────────────────
/**
 * Quem conduz a conversa antes de um humano (spec 021, FR-006):
 * - 'none'     ninguém: a conversa entra direto na fila da mesa do número;
 * - 'erp'      o chatbot de triagem do próprio ERP (menu, v8 Etapa 4);
 * - 'external' um bot de fora (o builder da Zeepchat) conduz até transferir
 *              pelo evento zeep.flow.handoff. Antes disso a conversa fica "com
 *              o bot", fora da fila; ao encerrar, o ERP devolve a sessão ao bot.
 */
export const WhatsappBotModeSchema = z.enum(['none', 'erp', 'external']);
export type WhatsappBotMode = z.infer<typeof WhatsappBotModeSchema>;

export const WHATSAPP_BOT_MODE_LABELS: Record<WhatsappBotMode, string> = {
  none: 'Sem bot',
  erp: 'Chatbot do ERP',
  external: 'Bot externo (Zeepchat)',
};

/** limites do mapeamento fila → mesa do bot externo (API valida, UI mostra) */
export const BOT_HANDOFF_QUEUE_MAX = 64;
export const BOT_HANDOFF_QUEUES_MAX = 20;

/**
 * Fila do bot externo → mesa do ERP. `queue` é o nome que o fluxo põe no
 * bloco de transferência (o campo `queue` do evento), comparado sem caixa e
 * sem espaços nas pontas. Fila sem linha aqui, ou sem fila no evento, vai para
 * a mesa do número.
 */
export const WhatsappBotHandoffQueueSchema = z.object({
  queue: z.string().trim().min(1).max(BOT_HANDOFF_QUEUE_MAX),
  deskId: z.number().int().positive(),
});
export type WhatsappBotHandoffQueue = z.infer<typeof WhatsappBotHandoffQueueSchema>;

/** a chave de comparação das filas — a mesma no PUT (duplicadas) e no evento */
export function botQueueKey(queue: string): string {
  return queue.trim().toLowerCase();
}

// ── canais/números (GET /whatsapp/channels, PUT /whatsapp/channels/:id) ─────
/**
 * Por onde o número fala (spec 028, FR-003): a Meta direto (Cloud API) ou a
 * Blip, pelo adapter do broker (spec 025). Explícito no canal — o padrão é a
 * Meta, e nada no resto do ERP decide pelo formato do phone_number_id.
 */
export const WhatsappProviderSchema = z.enum(['meta', 'blip']);
export type WhatsappProvider = z.infer<typeof WhatsappProviderSchema>;

export const WHATSAPP_PROVIDER_LABELS: Record<WhatsappProvider, string> = {
  meta: 'Meta',
  blip: 'Blip',
};

/** nome do canal ("PoolTrack") — curto: aparece no cabeçalho e na lista */
export const WHATSAPP_CHANNEL_NAME_MAX = 60;

/**
 * "Por qual número da empresa a pessoa fala" (spec 028, FR-003): nome do canal,
 * o provedor quando NÃO é a Meta, e o número — "PoolTrack · Blip · +55 31
 * 3527-5550". `short` (a lista): o nome, ou o número quando o canal não tem
 * nome, e o provedor não-Meta. Fonte única do cabeçalho, da lista e da barra.
 */
export function whatsappChannelLabel(
  channel: { name: string | null; provider: WhatsappProvider; displayPhone: string },
  opts: { short?: boolean } = {}
): string {
  const name = channel.name?.trim() || null;
  const provider = channel.provider === 'meta' ? null : WHATSAPP_PROVIDER_LABELS[channel.provider];
  const parts = opts.short
    ? [name ?? channel.displayPhone, provider]
    : [name, provider, channel.displayPhone];
  return parts.filter((p): p is string => Boolean(p)).join(' · ');
}

/** texto padrão do aviso de desistência do chatbot (spec 013) */
export const CHATBOT_HANDOFF_DEFAULT =
  'Não consegui entender sua opção. Um atendente vai continuar com você por aqui.';

export const WhatsappChannelSchema = z.object({
  id: z.number().int(),
  /** id do número na Meta (chave do roteamento inbound) */
  phoneNumberId: z.string(),
  /** id interno do manager zeepchat */
  publicId: z.string().nullable(),
  displayPhone: z.string(),
  /** nome do canal (spec 028) — null = só o número aparece */
  name: z.string().nullable(),
  /** Meta direto ou Blip (spec 028) — aparece quando não é a Meta */
  provider: WhatsappProviderSchema,
  /** mesa que recebe os chats deste número */
  deskId: z.number().int().nullable(),
  active: z.boolean(),
  /** quem conduz a conversa antes de um humano (spec 021) */
  botMode: WhatsappBotModeSchema,
  /** modo 'external': fila do evento de transferência → mesa (null = mesa do número) */
  botHandoffQueues: z.array(WhatsappBotHandoffQueueSchema).nullable(),
  // chatbot (v8 Etapa 4) — config por canal; runtime no inbound WhatsApp.
  // spec 021: `chatbotEnabled` é o botMode visto por quem ainda não o conhece —
  // true ⟺ botMode 'erp' (nunca uma segunda fonte)
  chatbotEnabled: z.boolean(),
  /** saudação enviada antes do menu (null = sem saudação) */
  chatbotGreeting: z.string().nullable(),
  /** menu numerado (null = não configurado; bot fica inerte sem menu) */
  chatbotMenu: z.array(WhatsappChatbotMenuOptionSchema).nullable(),
  /** estilo do menu (v10 Etapa 1): lista interativa nativa ou texto numerado */
  chatbotMenuStyle: WhatsappChatbotMenuStyleSchema,
  /** resposta fora do expediente da mesa do canal (null = silêncio) */
  chatbotOffhoursMessage: z.string().nullable(),
  /** spec 073 (P2): pesquisa de satisfação (1 a 5) ao finalizar a conversa */
  csatEnabled: z.boolean(),
  /**
   * Aviso ao cliente quando o bot DESISTE (resposta fora do menu duas vezes,
   * spec 013). null = usa CHATBOT_HANDOFF_DEFAULT — o cliente nunca fica sem
   * saber que um humano vai continuar.
   */
  chatbotHandoffMessage: z.string().nullable(),
});
export type WhatsappChannel = z.infer<typeof WhatsappChannelSchema>;

export const WhatsappChannelPatchBodySchema = z
  .object({
    deskId: z.number().int().positive().nullable(),
    active: z.boolean(),
    // spec 028: como o número aparece na conversa ("PoolTrack · Blip · …")
    name: z.string().trim().min(1).max(WHATSAPP_CHANNEL_NAME_MAX).nullable(),
    provider: WhatsappProviderSchema,
    // spec 021: o modo manda. `chatbotEnabled` sozinho (cliente antigo) vira
    // modo no use-case; os dois juntos e discordando é 422 bot_mode_conflict
    botMode: WhatsappBotModeSchema,
    // deskIds conferidos no use-case (422 unknown_desk_ids); fila repetida
    // (sem caixa) é 422 duplicate_bot_queue
    botHandoffQueues: z.array(WhatsappBotHandoffQueueSchema).max(BOT_HANDOFF_QUEUES_MAX).nullable(),
    // chatbot (v8 Etapa 4): 1..10 opções; labels 1..72; reply ≤1024. A
    // existência dos deskIds no tenant é checada no use-case (422 unknown_desk_ids)
    chatbotEnabled: z.boolean(),
    chatbotGreeting: z.string().trim().min(1).max(CHATBOT_REPLY_MAX).nullable(),
    chatbotMenu: z
      .array(WhatsappChatbotMenuOptionSchema)
      .min(1)
      .max(CHATBOT_MENU_MAX_OPTIONS)
      .nullable(),
    chatbotMenuStyle: WhatsappChatbotMenuStyleSchema,
    chatbotOffhoursMessage: z.string().trim().min(1).max(CHATBOT_REPLY_MAX).nullable(),
    // spec 073 (P2): vale em qualquer modo do bot
    csatEnabled: z.boolean(),
    chatbotHandoffMessage: z.string().trim().min(1).max(CHATBOT_REPLY_MAX).nullable(),
  })
  .partial()
  .strict();
export type WhatsappChannelPatchBody = z.infer<typeof WhatsappChannelPatchBodySchema>;

// ── Embedded Signup (POST /whatsapp/signup) ─────────────────────────────────
export const WhatsappSignupBodySchema = z.object({
  /** code do FB.login (trocado por token DENTRO do manager, nunca no ERP) */
  code: z.string().min(5),
  flow: z.enum(['cloud_api', 'coexistence']),
  /** ids observados no postMessage WA_EMBEDDED_SIGNUP FINISH (best-effort) */
  observedIds: z
    .object({
      wabaId: z.string().optional(),
      phoneNumberId: z.string().optional(),
    })
    .optional(),
});
export type WhatsappSignupBody = z.infer<typeof WhatsappSignupBodySchema>;

// ── placeholders {{n}} de template HSM (v8 Etapa 3) ─────────────────────────
/**
 * Gramática ÚNICA dos placeholders posicionais {{n}} (1-based) dos templates
 * HSM — a contagem/render server-side da API e o picker do web importam DAQUI
 * (re-implementar a regex foi vetado em review: três variantes já divergiam).
 * Espaços internos tolerados: `{{ 2 }}` ≡ `{{2}}` — o mesmo afrouxamento do
 * renderTemplate de e-mail.
 */
export const TEMPLATE_PLACEHOLDER_RE = /\{\{\s*(\d+)\s*\}\}/g;

/** maior índice {{n}} do body — a Meta numera 1..n sem buracos */
export function templateParamCount(body: string): number {
  let max = 0;
  for (const match of body.matchAll(TEMPLATE_PLACEHOLDER_RE)) {
    const n = Number(match[1]);
    if (Number.isFinite(n) && n > max) max = n;
  }
  return max;
}

/**
 * Substitui {{1}}..{{n}} por params[n-1]. Placeholder SEM valor fica INTACTO —
 * semântica deliberadamente DIFERENTE do renderTemplate de e-mail (que apaga a
 * variável ausente): o fluxo real valida params.length === templateParamCount
 * ANTES de renderizar, então o caso só aparece em previews, e mostrar o {{n}}
 * cru é mais honesto que sumir com ele. Valores substituídos NÃO são
 * re-escaneados.
 */
export function renderTemplateParams(body: string, params: readonly string[]): string {
  return body.replace(TEMPLATE_PLACEHOLDER_RE, (match, n: string) => {
    const value = params[Number(n) - 1];
    return value === undefined ? match : value;
  });
}

// ── templates HSM (GET /whatsapp/templates, v8 Etapa 3) ─────────────────────
export const WhatsappTemplateSchema = z.object({
  /** nome Meta do template (chave junto com language) */
  name: z.string(),
  /** código de idioma da Meta (ex.: pt_BR) */
  language: z.string(),
  category: z.string().nullable(),
  /** texto do componente BODY (placeholders {{n}} intactos) */
  body: z.string(),
  /** quantidade de placeholders {{n}} do body */
  params: z.number().int(),
  /**
   * os números (phone_number_id dos canais ativos) que têm o modelo — a tela
   * oferece, para o canal escolhido, só os dele (spec 025: o número da Blip
   * tem os modelos da conta da Blip, e o da Meta os da conta da Meta)
   */
  phoneNumberIds: z.array(z.string()),
});
export type WhatsappTemplate = z.infer<typeof WhatsappTemplateSchema>;

/** um número cujos modelos não carregaram — o resto da lista segue */
export const WhatsappTemplateFailureSchema = z.object({
  phoneNumberId: z.string(),
  provider: WhatsappProviderSchema,
  /** código curto (`blip_templates_failed`, `not_blip_number`, `TimeoutError`…) */
  error: z.string(),
});
export type WhatsappTemplateFailure = z.infer<typeof WhatsappTemplateFailureSchema>;

export const WhatsappTemplatesResponseSchema = z.object({
  templates: z.array(WhatsappTemplateSchema),
  failures: z.array(WhatsappTemplateFailureSchema),
});
export type WhatsappTemplatesResponse = z.infer<typeof WhatsappTemplatesResponseSchema>;

/**
 * Os modelos e as falhas de UM número (o canal escolhido na tela). `null` =
 * sem filtro: a lista do tenant inteiro. Modelo com `phoneNumberIds: null` vem
 * de uma API anterior a esta informação (o web pode publicar antes dela) e
 * vale para qualquer número, como antes.
 */
export function whatsappTemplatesOfNumber<
  T extends { phoneNumberIds: readonly string[] | null },
  F extends { phoneNumberId: string },
>(
  list: { templates: readonly T[]; failures: readonly F[] } | undefined,
  phoneNumberId: string | null
): { templates: T[]; failures: F[] } {
  const templates = [...(list?.templates ?? [])];
  const failures = [...(list?.failures ?? [])];
  if (phoneNumberId === null) return { templates, failures };
  return {
    templates: templates.filter(
      (t) => t.phoneNumberIds === null || t.phoneNumberIds.includes(phoneNumberId)
    ),
    failures: failures.filter((f) => f.phoneNumberId === phoneNumberId),
  };
}

// ── janela de 24h (GET /whatsapp/window?chatId=, v8 Etapa 3) ────────────────
export const WhatsappWindowQuerySchema = z.object({
  chatId: z.coerce.number().int().positive(),
});
export const WhatsappWindowResponseSchema = z.object({
  /** true = input livre aberto; false = só template (janela fechada) */
  open: z.boolean(),
});
export type WhatsappWindowResponse = z.infer<typeof WhatsappWindowResponseSchema>;

// ── campanhas (v10 Etapa 2) ─────────────────────────────────────────────────
/** teto de destinatários por campanha (create responde 422 too_many_recipients) */
export const WA_CAMPAIGN_MAX_RECIPIENTS = 10_000;

/**
 * Estados da campanha: draft (recém-criada, editável/apagável) → running
 * (scheduler enviando) ⇄ paused → done (nenhum pending restante) | canceled
 * (fechada; os pending restantes ficam pending — nunca serão enviados).
 */
export const WaCampaignStatusSchema = z.enum(['draft', 'running', 'paused', 'done', 'canceled']);
export type WaCampaignStatus = z.infer<typeof WaCampaignStatusSchema>;

export const CAMPAIGN_STATUS_LABELS: Record<WaCampaignStatus, string> = {
  draft: 'Rascunho',
  running: 'Em execução',
  paused: 'Pausada',
  done: 'Concluída',
  canceled: 'Cancelada',
};

/**
 * Estados do destinatário: pending → sent (broker aceitou; sent_at) →
 * delivered → read (acks via inbound, ref `camp:<c>:<r>`); denied (denylist do
 * broker) e failed (transporte/rejeição) são TERMINAIS — campanha nunca
 * re-tenta destinatário individual (semântica do dispatch do manager).
 */
export const WaCampaignRecipientStatusSchema = z.enum([
  'pending',
  'sent',
  'delivered',
  'read',
  'denied',
  'failed',
]);
export type WaCampaignRecipientStatus = z.infer<typeof WaCampaignRecipientStatusSchema>;

export const CAMPAIGN_RECIPIENT_STATUS_LABELS: Record<WaCampaignRecipientStatus, string> = {
  pending: 'Pendente',
  sent: 'Enviado',
  delivered: 'Entregue',
  read: 'Lido',
  denied: 'Negado (denylist)',
  failed: 'Falhou',
};

/**
 * Normalização ÚNICA de telefone de campanha (API valida no create, UI usa no
 * wizard): tira o '+' e separadores comuns (espaço, hífen, parênteses, ponto)
 * e exige 8–15 dígitos (E.164 sem o '+'). null = inválido.
 */
export function normalizeWaPhone(raw: string): string | null {
  const digits = raw.trim().replace(/^\+/, '').replace(/[\s().-]/g, '');
  return /^\d{8,15}$/.test(digits) ? digits : null;
}

/** linha de campanha (GET /whatsapp/campaigns e detalhe) */
export const WaCampaignSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  channelId: z.number().int(),
  templateName: z.string(),
  templateLanguage: z.string(),
  status: WaCampaignStatusSchema,
  totalRecipients: z.number().int(),
  /** contadores por lote do scheduler (o detalhe traz o progresso VIVO).
   *  sentCount conta TENTADOS (aceitos pelo broker): um ack 'failed' posterior
   *  da Meta soma em failedCount SEM decrementar sentCount — some os dois e
   *  pode passar de totalRecipients; o retrato fiel é o progress. */
  sentCount: z.number().int(),
  deniedCount: z.number().int(),
  failedCount: z.number().int(),
  createdById: z.number().int().nullable(),
  createdAt: Dateish,
  startedAt: Dateish.nullable(),
  finishedAt: Dateish.nullable(),
});
export type WaCampaign = z.infer<typeof WaCampaignSchema>;

/** progresso vivo (agregação sobre os destinatários) — GET /campaigns/:id */
export const WaCampaignProgressSchema = z.object({
  pending: z.number().int(),
  sent: z.number().int(),
  delivered: z.number().int(),
  read: z.number().int(),
  denied: z.number().int(),
  failed: z.number().int(),
});
export type WaCampaignProgress = z.infer<typeof WaCampaignProgressSchema>;

export const WaCampaignDetailSchema = WaCampaignSchema.extend({
  progress: WaCampaignProgressSchema,
});
export type WaCampaignDetail = z.infer<typeof WaCampaignDetailSchema>;

/**
 * POST /whatsapp/campaigns. O teto de 10k e a validação de telefone/params
 * (contagem == params do template, por linha) ficam no use-case — os erros
 * específicos (too_many_recipients, invalid_phone {row}, params_mismatch
 * {row}) são mais úteis que um invalid_body genérico do zod.
 */
export const CampaignCreateBodySchema = z.object({
  name: z.string().trim().min(1).max(120),
  channelId: z.number().int().positive(),
  templateName: z.string().min(1),
  templateLanguage: z.string().min(1),
  recipients: z
    .array(
      z.object({
        phone: z.string().min(1).max(32),
        params: z.array(z.string().min(1).max(1024)).default([]),
      })
    )
    .min(1),
});
export type CampaignCreateBody = z.infer<typeof CampaignCreateBodySchema>;

// ── config pública do signup (GET /whatsapp/config) ─────────────────────────
export const WhatsappConfigSchema = z.object({
  fbAppId: z.string(),
  fbConfigId: z.string(),
  fbConfigIdCoexistence: z.string(),
});
export type WhatsappConfig = z.infer<typeof WhatsappConfigSchema>;

// ── interruptor da Blip (spec 025, fase 3) ──────────────────────────────────
/**
 * "Devolver todos os contatos ao fluxo da Blip": o interruptor geral do número
 * da Blip, por tenant. Primeiro PARA o disparo pela Blip (a marca persistida
 * que o ciclo, o envio e a outbox conferem), depois pede ao broker que desmarque
 * TODOS os contatos marcados de cada número Blip do tenant, até ele responder
 * 200. Religar o disparo é a segunda ação. Toda ação fica no registro (quem,
 * quando, e as contagens do broker).
 */
export const BLIP_SWITCH_ACTIONS = ['stop', 'unmark_all', 'resume'] as const;
export const BlipSwitchActionSchema = z.enum(BLIP_SWITCH_ACTIONS);
export type BlipSwitchAction = z.infer<typeof BlipSwitchActionSchema>;

/** o que o broker contou num unmark-all (as mesmas chaves da resposta dele) */
export const BlipUnmarkCountsSchema = z.object({
  total: z.number().int(),
  unmarked: z.number().int(),
  skipped: z.number().int(),
  failed: z.number().int(),
});
export type BlipUnmarkCounts = z.infer<typeof BlipUnmarkCountsSchema>;

/** o desfecho do unmark-all de UM número Blip, depois das tentativas */
export const BlipSwitchChannelRunSchema = z.object({
  channelId: z.number().int(),
  phoneNumberId: z.string(),
  label: z.string(),
  /**
   * done = o broker respondeu 200 (nenhum contato ficou marcado); failed = as
   * tentativas acabaram sem 200 — há contato marcado, e a ação pode ser
   * repetida (é idempotente); skipped = WHATSAPP_MODE=off ou integração inativa
   */
  outcome: z.enum(['done', 'failed', 'skipped']),
  attempts: z.number().int(),
  /** as contagens da ÚLTIMA resposta com corpo (null = nenhuma resposta) */
  counts: BlipUnmarkCountsSchema.nullable(),
  /** o código curto do último erro (`http_502`, `blip_disabled`…) */
  error: z.string().nullable(),
});
export type BlipSwitchChannelRun = z.infer<typeof BlipSwitchChannelRunSchema>;

export const BlipSwitchLogEntrySchema = z.object({
  id: z.number().int(),
  action: BlipSwitchActionSchema,
  userName: z.string().nullable(),
  at: Dateish,
  phoneNumberId: z.string().nullable(),
  httpStatus: z.number().int().nullable(),
  counts: BlipUnmarkCountsSchema.nullable(),
  error: z.string().nullable(),
});
export type BlipSwitchLogEntry = z.infer<typeof BlipSwitchLogEntrySchema>;

/** GET /whatsapp/blip-switch */
export const BlipSwitchStateSchema = z.object({
  /** o disparo pela Blip está parado (acionado e ainda não religado) */
  dispatchStopped: z.boolean(),
  stoppedAt: Dateish.nullable(),
  stoppedBy: z.string().nullable(),
  /** os números Blip do tenant — o unmark-all percorre todos */
  channels: z.array(
    z.object({
      id: z.number().int(),
      phoneNumberId: z.string(),
      label: z.string(),
      active: z.boolean(),
    })
  ),
  /** desmarcações de UM contato ainda na fila (fim de conversa, ticket fechado) */
  pendingUnmarks: z.number().int(),
  /** desmarcações que desistiram nos últimos 7 dias — o contato pode seguir marcado */
  failedUnmarks: z.number().int(),
  /** as últimas ações do interruptor, mais nova primeiro */
  log: z.array(BlipSwitchLogEntrySchema),
});
export type BlipSwitchState = z.infer<typeof BlipSwitchStateSchema>;

/** POST /whatsapp/blip-switch/stop */
export const BlipSwitchStopResultSchema = z.object({
  /** true = todo número Blip respondeu 200 (ou não havia o que desmarcar) */
  complete: z.boolean(),
  runs: z.array(BlipSwitchChannelRunSchema),
  state: BlipSwitchStateSchema,
});
export type BlipSwitchStopResult = z.infer<typeof BlipSwitchStopResultSchema>;
